import type { User } from "@supabase/supabase-js";
import type { UserProfile } from "./authManager";

export type AdminDebugAccessReason =
  | "MISSING_AUTH_BACKEND"
  | "NO_USER"
  | "GUEST_USER"
  | "CLAIMS_GRANTED"
  | "PROFILE_GRANTED"
  | "ALLOWLIST_GRANTED"
  | "CLAIMS_INVALID"
  | "DENIED";

export interface AdminDebugAccess {
  canUseDebugSettings: boolean;
  reason: AdminDebugAccessReason;
  source: "none" | "claims" | "profile" | "allowlist";
}

interface ResolveAdminDebugAccessInput {
  user: Pick<User, "id" | "email" | "app_metadata" | "user_metadata"> | null;
  profile: UserProfile | null;
  authBackendAvailable: boolean;
  allowlist?: readonly string[];
}

const DEFAULT_DENIED: AdminDebugAccess = {
  canUseDebugSettings: false,
  reason: "DENIED",
  source: "none"
};

function normalizeAllowlist(allowlist: readonly string[] | undefined): Set<string> {
  const values = new Set<string>();
  for (const raw of allowlist ?? []) {
    const next = raw.trim().toLowerCase();
    if (next.length > 0) {
      values.add(next);
    }
  }
  return values;
}

function hasTruthyClaim(claims: unknown): boolean {
  if (typeof claims === "boolean") return claims;
  if (typeof claims === "number") return claims === 1;
  if (typeof claims === "string") {
    const normalized = claims.trim().toLowerCase();
    return normalized === "1" || normalized === "true" || normalized === "admin";
  }
  return false;
}

function readBooleanField(record: Record<string, unknown>, key: string): boolean | null {
  if (!(key in record)) {
    return null;
  }
  const value = record[key];
  if (value === true || value === false) {
    return value;
  }
  if (typeof value === "number") {
    return value === 1;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1") return true;
    if (normalized === "false" || normalized === "0") return false;
  }
  return null;
}

function resolveClaimsAdminAccess(user: Pick<User, "app_metadata" | "user_metadata">): {
  granted: boolean;
  malformed: boolean;
} {
  const containers = [user.app_metadata, user.user_metadata];
  let malformed = false;

  for (const container of containers) {
    if (container == null) {
      continue;
    }
    if (typeof container !== "object") {
      malformed = true;
      continue;
    }

    const obj = container as Record<string, unknown>;
    const role = obj.role;
    if (typeof role === "string" && role.trim().toLowerCase() === "admin") {
      return { granted: true, malformed };
    }

    const roles = obj.roles;
    if (Array.isArray(roles)) {
      const normalizedRoles = roles
        .filter((value): value is string => typeof value === "string")
        .map((value) => value.trim().toLowerCase());
      if (normalizedRoles.includes("admin") || normalizedRoles.includes("debug_admin")) {
        return { granted: true, malformed };
      }
      if (roles.some((value) => typeof value !== "string")) {
        malformed = true;
      }
    } else if (roles != null) {
      malformed = true;
    }

    const directFlags = ["debug_admin", "is_admin", "admin", "can_use_debug_settings"];
    for (const key of directFlags) {
      const value = readBooleanField(obj, key);
      if (value === true) {
        return { granted: true, malformed };
      }
      if (value == null && key in obj) {
        malformed = true;
      }
    }

    const claims = obj.claims;
    if (claims != null) {
      if (typeof claims !== "object") {
        malformed = true;
      } else {
        const claimRecord = claims as Record<string, unknown>;
        if (
          hasTruthyClaim(claimRecord.debug_admin)
          || hasTruthyClaim(claimRecord.admin)
          || hasTruthyClaim(claimRecord.can_use_debug_settings)
        ) {
          return { granted: true, malformed };
        }
      }
    }
  }

  return { granted: false, malformed };
}

function resolveProfileAdminAccess(profile: UserProfile | null): boolean {
  if (profile == null) {
    return false;
  }
  const raw = profile as unknown as Record<string, unknown>;
  return (
    hasTruthyClaim(raw.debugAdmin)
    || hasTruthyClaim(raw.debug_admin)
    || hasTruthyClaim(raw.isAdmin)
    || hasTruthyClaim(raw.is_admin)
  );
}

function resolveAllowlistAccess(
  user: Pick<User, "id" | "email">,
  profile: UserProfile | null,
  allowlist: Set<string>
): boolean {
  if (allowlist.size === 0) {
    return false;
  }
  const candidates = [
    user.id,
    user.email ?? "",
    profile?.id ?? "",
    profile?.username ?? ""
  ]
    .map((value) => value.trim().toLowerCase())
    .filter((value) => value.length > 0);
  return candidates.some((value) => allowlist.has(value));
}

export function resolveAdminDebugAccess(input: ResolveAdminDebugAccessInput): AdminDebugAccess {
  if (!input.authBackendAvailable) {
    return {
      canUseDebugSettings: false,
      reason: "MISSING_AUTH_BACKEND",
      source: "none"
    };
  }

  const user = input.user;
  if (user == null) {
    return {
      canUseDebugSettings: false,
      reason: "NO_USER",
      source: "none"
    };
  }

  if (user.id === "guest-local" || input.profile?.provider === "guest") {
    return {
      canUseDebugSettings: false,
      reason: "GUEST_USER",
      source: "none"
    };
  }

  const claimsResolution = resolveClaimsAdminAccess(user);
  if (claimsResolution.granted) {
    return {
      canUseDebugSettings: true,
      reason: "CLAIMS_GRANTED",
      source: "claims"
    };
  }

  if (resolveProfileAdminAccess(input.profile)) {
    return {
      canUseDebugSettings: true,
      reason: "PROFILE_GRANTED",
      source: "profile"
    };
  }

  const allowlist = normalizeAllowlist(input.allowlist);
  if (resolveAllowlistAccess(user, input.profile, allowlist)) {
    return {
      canUseDebugSettings: true,
      reason: "ALLOWLIST_GRANTED",
      source: "allowlist"
    };
  }

  if (claimsResolution.malformed) {
    return {
      canUseDebugSettings: false,
      reason: "CLAIMS_INVALID",
      source: "none"
    };
  }

  return DEFAULT_DENIED;
}
